import { ApplicationConfig, inject, provideAppInitializer, provideEnvironmentInitializer, provideZonelessChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { HttpErrorHandler, MessageService, services } from '@app/core/services';
import { provideRouter, RouteReuseStrategy, withRouterConfig } from '@angular/router';
import { AccountSwitchService } from '@app/common/services/account-switch.service';
import { AppInitializerService } from '@app/common/services/app-initializer.service';
import { AppRouteReuseStrategy } from '@app/core/routing/app-route-reuse-strategy';
import { CentralHttpInterceptor } from '@app/core/http-interceptors/central-http-interceptor.service';
import { DataStorage } from '@app/core/services/data-storage.service';
import { DatePipe } from '@angular/common';
import { httpActivityInterceptor } from '@app/core/http-interceptors/http-activity.interceptor';
import { IcLara } from './app.theme';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHighcharts } from 'highcharts-angular';
import { providePrimeNG } from 'primeng/config';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService } from '@ngx-translate/core';
import { routes } from './app.routes';

// What AppModule, AppRoutingModule and CoreModule provided, as one application config
// (AB#23257 slice 14, ADR 0016). Order follows the old module: router, animations, the core
// singletons, then the app's own providers with their original notes.
export const appConfig: ApplicationConfig = {
    providers: [
        // No zone.js (AB#23266, ADR 0008). Change detection runs when a signal a template reads changes, on a
        // template event, on markForCheck(), or when an async pipe emits. A plain field set in a subscribe() or a
        // timer does not redraw the page, so view state is held in signals (CODING-STANDARDS Section 5).
        provideZonelessChangeDetection(),
        // AppRoutingModule used RouterModule.forRoot(routes, { useHash: false, onSameUrlNavigation: 'reload' });
        // useHash false is the default.
        provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
        // Route-scoped re-creation (recreateOnNavigate, shell) in place of per-component shouldReuseRoute overrides.
        { provide: RouteReuseStrategy, useExisting: AppRouteReuseStrategy },
        // The animations renderer loads on first use, not in the initial bundle.
        provideAnimationsAsync(),
        // CoreModule's providers. The module guarded itself against a second import with
        // throwIfAlreadyLoaded; a standalone app has one root injector, so the guard went with it.
        ...services,
        HttpErrorHandler,
        MessageService,
        DataStorage,
        provideAppInitializer(() => inject(AppInitializerService).load()),
        // Forces AccountSwitchService into existence at app start, in every session, rather than leaving it to
        // whichever reader of AccountSessionState happens to mount first. Customers get it anyway, as soon as
        // either toolbar's ic-toolbar-account-select is created; this closes the gap for a Toro session, and for
        // any phase-3 reader built in parallel that does not itself inject AccountSwitchService (ER-1535 M1,
        // PI-ICNT-SW-E172 decision log - architecture).
        provideEnvironmentInitializer(() => inject(AccountSwitchService)),
        { provide: HTTP_INTERCEPTORS, useClass: CentralHttpInterceptor, multi: true },
        DatePipe,
        // httpActivityInterceptor only counts requests in flight, for the account-switch dissolve (HttpActivityService).
        provideHttpClient(withXhr(), withInterceptorsFromDi(), withInterceptors([httpActivityInterceptor])),
        // ngx-translate 18: TranslatePipe is gone; the service is provided
        // here and the loader rides in through the config's loader slot.
        // Defaults are unchanged: /assets/i18n/*.json
        provideTranslateService({ loader: provideTranslateHttpLoader() }),
        // PrimeNG 18: resources/* theme CSS no longer exists; the theme is the
        // design-token Lara preset (descendant of lara-light-blue) rendered into
        // the 'primeng' CSS layer, with the app's overrides in the
        // 'app-overrides' layer declared above it (ADR 0010).
        providePrimeNG({
            // Licence key is injected by the gitignored assets/license.js - written by the
            // developer locally and by the pipeline from the `primeUiLicenseKey` variable
            // (AB#23861). No longer dev-only, which is what ADR 0009 decision 2 recorded.
            // When undefined, PrimeNG paints its "Invalid PrimeUI License" notice; per PrimeTek's
            // licence terms that notice appears in development-mode builds only, which is why the
            // Dev environment showed it (angular.json `dev` sets optimization: false) and
            // production did not.
            license: (window as any).__primeUiLicense,
            theme: {
                preset: IcLara,
                options: {
                    // The app ships no dark theme. PrimeNG defaults
                    // darkModeSelector to 'system', which resolves to
                    // @media (prefers-color-scheme: dark) and flips PrimeNG
                    // surfaces (table backgrounds, inputs) dark on a dark-mode
                    // OS while the app's own SCSS stays light. Pin to light.
                    darkModeSelector: 'none',
                    // The order must list every layer, lowest first. PrimeNG injects this statement
                    // before styles.css loads, and the first @layer statement a page sees fixes the
                    // order: a layer it leaves out is appended later and ends up on TOP. With just
                    // 'primeng, app-overrides' here, styles.scss's 'vendor' became the highest layer
                    // and Bootstrap beat PrimeNG harder than before (AB#23486).
                    cssLayer: {
                        name: 'primeng',
                        order: 'vendor, primeng, app-overrides',
                    },
                },
            },
        }),
        // highcharts-angular v5: the [Highcharts] input is gone; the instance is
        // provided once. Dynamic import resolves to the same module singleton the
        // chart components' side-effect module imports (heatmap etc.) register on.
        provideHighcharts({ instance: () => import('highcharts').then(m => m.default ?? m) }),
    ],
};
